/**
 * <once-reviews> (blocks/_once-product-reviews.liquid)
 *
 * A one-line review carousel. The reviews sit side by side in a strip, and the
 * strip slides left to the next review every data-interval seconds. Shoppers
 * can also swipe it (or drag it with a mouse) either way.
 *
 * To loop without sliding all the way back, a copy of the first review sits at
 * the end of the strip: after sliding onto the copy, the strip jumps (without
 * animating) to the real first review, which looks identical.
 *
 * It pauses while the pointer is over it, focus is inside it, a drag is under
 * way, or the tab is in the background.
 */
const SWIPE_THRESHOLD = 40;

class OnceReviews extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-once-reviews-viewport]');
    this.track = this.querySelector('[data-once-reviews-track]');
    if (!this.viewport || !this.track) return;

    this.count = this.track.children.length;
    this.interval = Number(this.dataset.interval || 4.5) * 1000;
    this.index = 0;
    this.hovered = false;
    this.focused = false;
    this.toggleAttribute('data-ready', true);
    if (this.count < 2) return;

    this.#appendLoopCopy();

    this.track.addEventListener('transitionend', (event) => {
      if (event.target === this.track && this.index === this.count) this.#jump(0);
    });

    this.addEventListener('pointerenter', () => (this.hovered = true));
    this.addEventListener('pointerleave', () => (this.hovered = false));
    this.addEventListener('focusin', () => (this.focused = true));
    this.addEventListener('focusout', () => (this.focused = false));
    this.#listenForSwipes();

    this.#start();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  next() {
    if (this.index >= this.count) this.#jump(0);
    this.#go(this.index + 1);
  }

  previous() {
    if (this.index <= 0) this.#jump(this.count);
    this.#go(this.index - 1);
  }

  /** Restarts the timer, so a review the shopper just swiped to gets its full time. */
  #start() {
    clearInterval(this.timer);
    this.timer = setInterval(() => {
      const paused = this.hovered || this.focused || this.hasAttribute('data-dragging') || document.hidden;
      if (!paused) this.next();
    }, this.interval);
  }

  /** A copy of the first review at the end, hidden from screen readers and the theme editor. */
  #appendLoopCopy() {
    const copy = /** @type {HTMLElement} */ (this.track.firstElementChild.cloneNode(true));
    copy.setAttribute('aria-hidden', 'true');
    copy.inert = true;
    for (const element of [copy, ...copy.querySelectorAll('*')]) {
      for (const name of element.getAttributeNames()) {
        if (name.startsWith('data-shopify')) element.removeAttribute(name);
      }
    }
    this.track.append(copy);
  }

  /** Slides to a review. An offset (in px) shifts the strip while it's being dragged. */
  #go(index, offset = 0) {
    this.index = index;
    this.track.style.transform = `translateX(calc(${-index * 100}% + ${offset}px))`;
  }

  /** Moves to a review instantly, with no slide. */
  #jump(index) {
    this.track.style.transition = 'none';
    this.#go(index);
    this.track.getBoundingClientRect();
    this.track.style.transition = '';
  }

  #listenForSwipes() {
    let startX = 0;
    let startY = 0;
    let offset = 0;
    let pointerId = null;

    this.viewport.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      offset = 0;
      if (this.index === this.count) this.#jump(0);
    });

    this.viewport.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!this.hasAttribute('data-dragging')) {
        // Mostly vertical: the shopper is scrolling the page, not swiping.
        if (Math.abs(dx) < 6 || Math.abs(dy) > Math.abs(dx)) return;
        this.toggleAttribute('data-dragging', true);
        this.viewport.setPointerCapture(pointerId);
      }

      offset = dx;
      this.#go(this.index, offset);
    });

    const end = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (!this.hasAttribute('data-dragging')) return;
      this.removeAttribute('data-dragging');

      if (offset < -SWIPE_THRESHOLD) this.next();
      else if (offset > SWIPE_THRESHOLD) this.previous();
      else this.#go(this.index);
      this.#start();
    };

    this.viewport.addEventListener('pointerup', end);
    this.viewport.addEventListener('pointercancel', end);
  }
}

if (!customElements.get('once-reviews')) customElements.define('once-reviews', OnceReviews);
